import { useContext, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import * as Library from "../../../utils/Library";
import InfinityGridUsersCards from "../../common/cardContainers/InfinityGridUsersCards";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import { useQueryClient } from "react-query";
import { roleAccesse } from "../../../utils/roleManager";
import { useRef } from "react";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function UsersPage() {
    document.title = `${APP_TITLE} - Users`;

    // #region STATES
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { userData } = useContext(AppContext);
    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");
    const queryClient = useQueryClient();
    const gridRef = useRef(null);

    const navigate = useNavigate();
    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    //---------- Header Filters
    const [searchText, setSearchText] = useState("");
    const [allRetailersList, setAllRetailersList] = useState([]);
    const [filteredRetailers, setFilteredRetailers] = useState([]);
    const [requestEnabled, setRequestsEnabled] = useState(false);
    //-----
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [isSuspendPopupOpen, setIsSuspendPopupOpen] = useState(false);

    const [suspendInfo, setSuspendInfo] = useState({ name: "", firstName: "" });
    const [deleteInfo, setDeleteInfo] = useState({ id: "", name: "" });

    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const [listNumRows, setListNumRows] = useState(20);
    const filters = {
        filters: {
            retailBrandsIDs: filteredRetailers?.map(item => item?.value),
            searchText: searchText
        }
    };
    const infiniteGridQuerykey = ["RetailersConnectionsGrid", filters];
    const listQueryKey = ["RetailersConnectionsList", filters];
    // #endregion

    // #region METHODS
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    const onRetailersChange = selection => {
        if (!selection?.locked) {
            setFilteredRetailers([selection]);
            sessionStorage.setItem("currentRetailer", selection?.value || "");
        }
    };

    const onFiltersLoaded = data => {
        if (data?.retailBrands?.length) {
            const defaultRetailer =
                sessionStorage.getItem("currentRetailer") || localStorage.getItem("defaultRetailBrand");
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailerID = localStorage.getItem("defaultRetailBrand");
                let retailerName = item?.name;

                if (item?._id === defaultRetailerID) {
                    retailerName = `${item?.name} (default)`;
                }
                const restrictedRetailBrands = JSON.parse(localStorage.getItem("restrictedRetailBrandIDs") || "[]");
                const locked = !(editAccess || restrictedRetailBrands.includes(item?._id));
                return {
                    name: retailerName,
                    value: item?._id,
                    selected: item?._id === defaultRetailer,
                    locked: locked
                };
            });

            const defaultRetailerObj = retailBrands?.find(item => item?.value === defaultRetailer) || retailBrands?.[0];

            setAllRetailersList(retailBrands);
            setFilteredRetailers([defaultRetailerObj]);
        }
        setRequestsEnabled(true);
    };

    const onDeleteAction = item => {
        setDeleteInfo({ id: item?._id || "", name: item?.name || "" });
        setIsDeletePopupOpen(true);
    };

    const onDeleteConfirm = () => {
        const formData = { userID: deleteInfo?.id };
        Library.makeCommonPostRequest("Users", "deleteUser", formData, false).then(res => {
            if (res?.data?.status === 200) {
                Library.showToastMessage({
                    type: "success",
                    message: `${deleteInfo?.name} - user account has been deleted`,
                    title: "Account Deleted"
                });
                onClickRefreshAction();
            }
        });

        setIsDeletePopupOpen(false);
    };

    const onSuspendAction = item => {
        setSuspendInfo({
            name: item?.name || "",
            firstName: item?.firstName || "",
            id: item?._id || "",
            status: item?.status?.toLowerCase()
        });
        setIsSuspendPopupOpen(true);
    };

    const onSuspendConfirm = () => {
        const formData = {
            status: suspendInfo?.status === "suspended" ? "Active" : "Suspended",
            userID: suspendInfo?.id,
            organizationID: localStorage.getItem("currentOrganizationID")
        };
        Library.makeCommonPostRequest("Users", "updateUserStatus", formData, false).then(res => {
            if (res?.data?.status === 200) {
                const isSuspended = suspendInfo?.status !== "suspended";

                const title = isSuspended ? "Account Suspended" : "Account Reactivated";
                const message = isSuspended
                    ? `${suspendInfo?.name} account has been suspended`
                    : `${suspendInfo?.name} account is now active`;

                Library.showToastMessage({ type: "success", message: message, title: title });
                onClickRefreshAction();
            }
        });
        setIsSuspendPopupOpen(false);
    };

    const onEdit = item => {
        navigate(`/users/edit/${item?._id}`);
        // navigate
    };

    const onSearch = searchText => {
        setSearchText(searchText);
    };
    //#endregion METHODS

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders({
            props,
            onClickRefreshAction,
            allRetailersList,
            onRetailersChange,
            onFiltersLoaded,
            filteredRetailers,
            onSearch,
            editAccess,
            userRole
        });
    }, [props?.layoutType, filteredRetailers, allRetailersList, editAccess, userRole]);

    useEffect(() => {
        Library.removeQueries(queryClient, infiniteGridQuerykey);
        Library.removeQueries(queryClient, listQueryKey);
    }, []);
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "First Name",
            selector: row => row?.firstName ?? "--"
        },
        {
            name: "Last Name",
            selector: row => row?.lastName ?? "--"
        },
        {
            name: "Role",
            selector: row => row?.role ?? "--"
        },
        {
            name: "Stage Level",
            selector: row => (row?.accessLevel === "Organization level" ? "Account level" : row?.accessLevel) //TODO: Implement stage level
        },
        {
            name: "Phone number",
            selector: row => (row?.phoneNumber ? `${row?.phoneNumberCountryCode} ${row?.phoneNumber}` : "--")
        },
        {
            name: "Email",
            selector: row => row?.email ?? "--"
        }
    ];
    // #endregion

    return (
        <>
            <FullScreenOverlay show={isDeletePopupOpen}>
                <DialogBoxComponent
                    heading="ATTENTION!"
                    headingColor="var(--text-icons-red-500)"
                    title={"Delete user account?"}
                    // message Text: [user] account will be deleted. [user] will not be able to login again.
                    message={`${deleteInfo?.name} account will be deleted. ${deleteInfo?.name} will not be able to login again.`}
                    onClickNo={() => setIsDeletePopupOpen(false)}
                    onClickYes={onDeleteConfirm}
                    cancelText="NO"
                    confirmText="YES"
                    confirmState="primary"
                    buttonWidth="150px"
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={isSuspendPopupOpen}>
                <DialogBoxComponent
                    heading="JUST CHECKING..."
                    title={
                        suspendInfo?.status !== "suspended"
                            ? `Are you sure you want to suspend ${suspendInfo?.name}?`
                            : "Reactivate user account?"
                    }
                    message={
                        suspendInfo?.status !== "suspended"
                            ? `${suspendInfo?.firstName} status will become SUSPENDED until you reactivate the account. ${suspendInfo?.firstName} will be logged out of their account. This may take up to 30 minutes to take effect. No email will be sent.`
                            : `${suspendInfo?.firstName} status will become ACTIVE. ${suspendInfo?.firstName} can now login to the account.`
                    }
                    onClickNo={() => setIsSuspendPopupOpen(false)}
                    onClickYes={onSuspendConfirm}
                    cancelText="NO"
                    confirmText="YES"
                    confirmState="primary"
                    buttonWidth="150px"
                />
            </FullScreenOverlay>

            <StyledPage>
                {viewLayoutStyle === "Grid" && (
                    <InfinityGridUsersCards
                        ref={gridRef}
                        minColumnWidth="238px"
                        defaultSecondTitle="Account:"
                        limit={10}
                        queryKey={infiniteGridQuerykey}
                        statusFromPath="status"
                        requestEnabled={requestEnabled}
                        filters={filters}
                        context="Users"
                        endpoint="getUsersData"
                        onDeleteAction={onDeleteAction}
                        onSuspendAction={onSuspendAction}
                        onEditAction={onEdit}
                        userRole={userRole}
                    />
                )}
                {viewLayoutStyle === "List" && (
                    <ListComponent
                        title="Users"
                        columns={listColumns}
                        api={{
                            useCommonPostRequest: true,
                            commonContext: "Users",
                            endpoint: "getUsersData",
                            formData: {
                                organizationID: localStorage.getItem("currentOrganizationID"),
                                limit: listNumRows,
                                searchText: searchText,
                                ...filters
                            }
                            //onSuccess: mapListData
                        }}
                        pageLimit={listNumRows}
                        currentPage={1}
                        queryKey={listQueryKey}
                        actions={{
                            includeActions: true,
                            onClickEdit: onEdit,
                            onClickRemove: onDeleteAction,
                            onClickSuspend: onSuspendAction,
                            extraActions: [{ title: "Details" }]
                        }}
                        //TODO: Add this new prop to the list os props
                        actionsrestrictions={{
                            editAccesseLowerRoleOnly: true,
                            removeAccesseLowerRoleOnly: true,
                            suspendAccesseLowerRoleOnly: true,
                            onlyEditHisOwnUser: true
                        }}
                        includeStatus={{
                            stickyStatus: true,
                            statusFromPath: "status",
                            statusColors: defaultStatusColors
                        }}
                        onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                        requestEnabled={requestEnabled}
                    />
                )}
            </StyledPage>
        </>
    );
}

function setupHeaders({
    props,
    onClickRefreshAction,
    allRetailersList,
    onRetailersChange,
    onFiltersLoaded,
    filteredRetailers,
    onSearch,
    editAccess,
    userRole
}) {
    const isMarketingAccount = [
        "Brand Marketing Admin",
        "Brand Marketing Manager",
        "Brand Marketing Assistant"
    ].includes(userRole);
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Search" /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/users", active: true, disabled: false, title: "Users" },
        { to: "/users/invitations", active: false, disabled: false, title: "INVITES" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disable: false, title: "View", icon: "eye" },
        {
            to: !isMarketingAccount && editAccess ? "/users/invite" : "#",
            active: false,
            disabled: isMarketingAccount,
            title: "Invite",
            icon: "mail"
        },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allRetailersList,
                              onDropdownChange: onRetailersChange,
                              type: "singleSelect",
                              selectedValue: filteredRetailers?.[0]?.name
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    disabled: true
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
            />
        )
    });
}
